import { createSelector } from 'reselect';
import { getMachine } from './getMachine';
import { getIsMESEnabled } from './getIsMESEnabled';
import { getMachineSettings } from './getMachineSettings';
import { getPostLaborUpdatesOnNats } from './getPostLaborUpdatesOnNats';
import {
  getIsOperatorSignedIn,
  getSignedInOperatorErpId,
} from './getSignedInOperator';

// TODO: resource.machineRef is being deprecated. The { resource: { machineRef } } branch below
// covers labor tickets whose machineRef is null and fall back to the linked resource's machineRef.
// Once labor ticket.machineRef is always populated, this fallback can be dropped.
/** @param {number|string} machineRef */
function whereMachineRefOrLegacyResourceLink(machineRef) {
  return {
    _or: [
      { machineRef: { _eq: machineRef } },
      {
        _and: [
          { machineRef: { _is_null: true } },
          { resource: { machineRef: { _eq: machineRef } } },
        ],
      },
    ],
  };
}

/** @param {number|string} machineRef @param {string} laborResourceId */
function whereMachineRefOrLaborResourceLink(machineRef, laborResourceId) {
  return {
    _or: [
      { machineRef: { _eq: machineRef } },
      {
        _and: [
          { machineRef: { _is_null: true } },
          { resourceId: { _eq: laborResourceId } },
        ],
      },
    ],
  };
}

// The current-ticket queries sort tickets without a clockOut first as open.
// A CLOSED ticket missing its clockOut (a sync inconsistency) would then
// outrank the real open ticket, only for getCurrentLaborTicket to hide it.
const NOT_CLOSED_WITHOUT_CLOCK_OUT = {
  _not: {
    _and: [{ state: { _eq: 'CLOSED' } }, { clockOut: { _is_null: true } }],
  },
};

/**
 * Which tickets can be the machine's current ticket. A machine can hold several
 * open tickets (e.g. another employee's reprocess ticket opened from the ERP),
 * so the only open ticket that counts is the signed-in operator's own (the
 * Unattended operator signs in with a run too); someone else's never becomes
 * their job. With nobody signed in, no open ticket counts: it most likely
 * didn't come from MM.
 * Closed tickets count for everyone, signed in or not (getCurrentLaborTicket
 * keeps just a keep-open one, e.g. a paused setup to resume).
 * @param {boolean} isSignedIn
 * @param {string|null} erpId - The signed-in operator's ERP id, once loaded
 */
function whereVisibleToOperator(isSignedIn, erpId) {
  const visible = [{ clockOut: { _is_null: false } }];
  if (isSignedIn && erpId != null) {
    visible.push({ personId: { _eq: erpId } });
  }
  return { _or: visible };
}

// Primitive inputs: the settings poll replaces the settings object with an equal
// one, which must not produce a new where (resubscribing, or a resync response
// discarded as stale).
const getMachineRef = (state) => {
  return getMachine(state)?.machineRef;
};

const getLaborResourceId = (state) => {
  return getMachineSettings(state)?.laborResourceId;
};

/**
 * Where clause matching the machine's labor tickets, whoever they belong to.
 */
export const getLaborTicketMachineWhere = createSelector(
  [getMachineRef, getIsMESEnabled, getLaborResourceId],
  (machineRef, isMesEnabled, laborResourceId) => {
    if (isMesEnabled && laborResourceId != null && laborResourceId !== '') {
      return whereMachineRefOrLaborResourceLink(machineRef, laborResourceId);
    }
    return whereMachineRefOrLegacyResourceLink(machineRef);
  }
);

/**
 * Where clause for the machine's current labor ticket queries
 * (SUBSCRIBE_LABOR_TICKETS, GET_CURRENT_MACHINE_LABOR_TICKET).
 * Scoped to the signed-in operator only in the NATS labor flow, where operators
 * drive the operator run; elsewhere the labor ticket drives everything and the
 * machine's ticket is current whoever it belongs to.
 */
export const getLaborTicketWhereForMachine = createSelector(
  [
    getLaborTicketMachineWhere,
    getPostLaborUpdatesOnNats,
    getIsOperatorSignedIn,
    getSignedInOperatorErpId,
  ],
  (machineWhere, natsFlowEnabled, isSignedIn, erpId) => {
    if (!natsFlowEnabled) {
      return { _and: [machineWhere, NOT_CLOSED_WITHOUT_CLOCK_OUT] };
    }
    return {
      _and: [
        machineWhere,
        NOT_CLOSED_WITHOUT_CLOCK_OUT,
        whereVisibleToOperator(isSignedIn, erpId),
      ],
    };
  }
);
